import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import Services from './Services';
import About from './About';
import Contact from './Contact';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiArrowRight, FiTrendingUp, FiUsers, FiHome, FiShield } = FiIcons;

const Home = () => {
  const stats = [
    { number: '500+', label: 'Properties Managed', icon: FiHome },
    { number: '98%', label: 'Client Satisfaction', icon: FiUsers },
    { number: '15+', label: 'Years Experience', icon: FiTrendingUp },
    { number: '24/7', label: 'Support Available', icon: FiShield }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "DGM Property Management",
    "description": "Calgary's premier property management company offering comprehensive residential and commercial property management services.",
    "url": "https://dgmpropertymanagement.ca",
    "telephone": "587-800-1215",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Calgary",
      "addressRegion": "Alberta",
      "addressCountry": "CA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "51.0447",
      "longitude": "-114.0719"
    },
    "openingHours": "Mo-Fr 09:00-17:00",
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    }
  };

  return (
    <>
      <SEOHead
        title="Property Management Calgary | DGM Property Management"
        description="DGM Property Management is Calgary's trusted partner for residential and commercial property management. 500+ properties managed with 98% client satisfaction. Call 587-800-1215 for expert service."
        keywords="property management Calgary, Calgary property manager, residential property management Calgary, commercial property management Calgary, Airdrie property management, Alberta property management"
        canonical="/"
        ogImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Hero Section */}
        <section id="home" className="pt-16 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <div className="space-y-4">
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-4xl md:text-6xl font-bold leading-tight"
                  >
                    Calgary's Premier <span className="text-blue-400 block">Property Management</span>
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-xl text-gray-300 max-w-2xl"
                  >
                    Trusted by 500+ Calgary property owners. We handle everything from tenant placement to maintenance, maximizing your investment returns with 15+ years of local expertise.
                  </motion.p>
                </div>

                {/* Contact Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-4 text-lg">
                    <SafeIcon icon={FiPhone} className="w-6 h-6 text-blue-400" />
                    <a href="tel:587-800-1215" className="hover:text-blue-400 transition-colors">
                      587-800-1215
                    </a>
                  </div>
                  <div className="flex items-center gap-4 text-lg">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-blue-400" />
                    <span>Calgary & Airdrie, Alberta</span>
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <motion.a
                    href="#contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Get Free Consultation
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </motion.a>
                  <motion.a
                    href="#services"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 text-center"
                  >
                    Our Services
                  </motion.a>
                </motion.div>
              </motion.div>

              {/* Hero Image */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Modern residential properties in Calgary managed by DGM Property Management"
                    className="w-full h-[600px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <SafeIcon icon={stat.icon} className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-2">{stat.number}</div>
                  <div className="text-blue-100">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <Services />

        {/* About Section */}
        <About />

        {/* Contact Section */}
        <Contact />
      </motion.main>
    </>
  );
};

export default Home;